import type { PeerComparison, PeerPlace } from '@/types/gmb';
import { formatDistance, formatNumber, orDash } from '@/pages/gmb-audit/utils/format';

function ValueCell({ value, unit }: { value: number | null; unit: string }) {
  if (value === null || value === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <span className="font-medium text-slate-900">
      {formatNumber(value)}
      {unit ? <span className="text-slate-400"> {unit}</span> : null}
    </span>
  );
}

export default function PeerSection({
  comparison,
  peers,
}: {
  comparison: PeerComparison[];
  peers: PeerPlace[];
}) {
  const rows = comparison || [];
  const list = peers || [];
  const empty = rows.length === 0 && list.length === 0;

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 pt-12 md:px-8 md:pt-14">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-teal-50 text-teal-700">
          <i className="ri-group-line" />
        </span>
        <h2 className="text-lg font-bold text-slate-900 md:text-[1.5rem]">同業比較</h2>
      </div>
      <p className="mb-5 text-sm text-slate-500">以相同主要類別與鄰近範圍的公開資料作為比較基準。</p>

      {empty ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center text-sm text-slate-500">
          目前沒有可比較的同業樣本，待資料來源串接後顯示。
        </div>
      ) : (
        <>
          <div className="scroll-x-clean rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold text-slate-500">
                  <th className="px-4 py-3">指標</th>
                  <th className="px-4 py-3">本店</th>
                  <th className="px-4 py-3">同業中位數</th>
                  <th className="px-4 py-3">樣本數</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.metricCode} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-400">{row.metricCode}</span>
                      <span className="ml-2 text-slate-700">{row.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <ValueCell value={row.targetValue} unit={row.unit} />
                    </td>
                    <td className="px-4 py-3">
                      <ValueCell value={row.peerMedian} unit={row.unit} />
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {row.peerSampleSize === null || row.peerSampleSize === undefined
                        ? '—'
                        : `${row.peerSampleSize} 家`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mb-3 mt-8 text-base font-bold text-slate-900">同業樣本清單</h3>
          {list.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center text-sm text-slate-500">
              尚無同業樣本資料。
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {list.map((peer) => (
                <div key={peer.placeId} className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="truncate text-sm font-semibold text-slate-900">{orDash(peer.name)}</p>
                  <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-slate-500">
                    <i className="ri-map-pin-line" />
                    {orDash(peer.address)}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400" />
                      {peer.rating === null || peer.rating === undefined ? '—' : peer.rating.toFixed(1)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-chat-3-line" />
                      {formatNumber(peer.reviewCount)} 則評論
                    </span>
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      <i className="ri-route-line" />
                      {formatDistance(peer.distanceMeters)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}